import { hexToRgb, resolveDividerColor } from "./luminance";

export interface LoginCardBranding {
  cardBackground?: string | null;
  cardOpacity?: number | null;
  cardBlur?: number | null;
}

export interface LoginCardColors {
  headingText?: string | null;
  bodyText?: string | null;
  loginButtonDisabledBackground?: string | null;
  loginButtonDisabledText?: string | null;
}

export function buildLoginCardCssRules(
  s: string,
  branding: LoginCardBranding | null | undefined,
  colors?: LoginCardColors | null | undefined,
): string[] {
  const rules: string[] = [];

  const rawBg = branding?.cardBackground;
  const customBackground =
    typeof rawBg === "string" && rawBg.length > 0 ? rawBg : undefined;

  const rawOpacity = branding?.cardOpacity;
  const opacity =
    typeof rawOpacity === "number" && !Number.isNaN(rawOpacity)
      ? Math.min(1, Math.max(0, rawOpacity))
      : undefined;

  const backgroundColor = resolveCardBackgroundColor(customBackground, opacity);
  if (backgroundColor !== undefined) {
    rules.push(
      `${s}[data-ui="login-card"] { background-color: ${backgroundColor}; }`,
    );
  }

  const rawBlur = branding?.cardBlur;
  if (typeof rawBlur === "number" && !Number.isNaN(rawBlur)) {
    const blur = Math.min(40, Math.max(0, rawBlur));
    if (blur > 0) {
      rules.push(
        `${s}[data-ui="login-card"] { backdrop-filter: blur(${blur}px); -webkit-backdrop-filter: blur(${blur}px); }`,
      );
    }
  }

  // "Body text" applies to the subtitle (below the h1 inside the header) and
  // the link buttons (gift, register, return policy). The subtitle is a plain
  // <p> inside [data-ui="header"], the only <p> there since the title is an
  // <h1>; link buttons are RedoButton hierarchy="link_underline" which reads
  // a separate design-system token, so we override it directly.
  const bodyText = colors?.bodyText;
  if (typeof bodyText === "string" && bodyText.length > 0) {
    rules.push(
      `${s}[data-ui="login-card"] [data-ui="header"] p ` +
        `{ color: ${bodyText}; }`,
    );
    rules.push(
      `${s}[data-ui="login-card"] button[data-hierarchy="link_underline"] ` +
        `{ color: ${bodyText}; }`,
    );
  }

  // "Heading text" applies to the h1 title, section headings (e.g. the
  // "Buyer info" / "Your info" labels on the gift-return form), and the
  // labels above input fields. Input labels render as
  // <Text textColor="secondary"> → they read --redo-colors-text-text-secondary-700,
  // so overriding that variable scoped to the login card recolors them
  // without leaking to other elements that explicitly read their own tokens.
  const headingText = colors?.headingText;
  if (typeof headingText === "string" && headingText.length > 0) {
    rules.push(
      `${s}[data-ui="login-card"] h1, ` +
        `${s}[data-ui="login-card"] [data-ui="section-heading"] ` +
        `{ color: ${headingText}; }`,
    );
    rules.push(
      `${s}[data-ui="login-card"] ` +
        `{ --redo-colors-text-text-secondary-700: ${headingText}; }`,
    );
  }

  const loginButtonDisabledProps: string[] = [];
  const loginButtonDisabledBackground = colors?.loginButtonDisabledBackground;
  if (
    typeof loginButtonDisabledBackground === "string" &&
    loginButtonDisabledBackground.length > 0
  ) {
    loginButtonDisabledProps.push(
      `background-color: ${loginButtonDisabledBackground};`,
    );
  }
  const loginButtonDisabledText = colors?.loginButtonDisabledText;
  if (
    typeof loginButtonDisabledText === "string" &&
    loginButtonDisabledText.length > 0
  ) {
    loginButtonDisabledProps.push(`color: ${loginButtonDisabledText};`);
  }
  if (loginButtonDisabledProps.length > 0) {
    rules.push(
      `${s}[data-ui="login-card"] button[data-hierarchy="primary"]:disabled ` +
        `{ ${loginButtonDisabledProps.join(" ")} }`,
    );
  }

  // Auto-pick a divider color that complements the card background. The
  // <Divider/> footer separator reads `--redo-colors-border-border-primary`,
  // which defaults to a near-black hairline that disappears on dark cards. We
  // only override when the merchant has set a custom card background — the
  // existing default is correct for the stock white card.
  const dividerColor = resolveDividerColor(customBackground);
  if (dividerColor !== undefined) {
    rules.push(
      `${s}[data-ui="login-card"] ` +
        `{ --redo-colors-border-border-primary: ${dividerColor}; }`,
    );
  }

  return rules;
}

function resolveCardBackgroundColor(
  customBackground: string | undefined,
  opacity: number | undefined,
): string | undefined {
  if (customBackground === undefined) {
    if (opacity === undefined || opacity >= 1) {
      return undefined;
    }
    return `rgba(255, 255, 255, ${opacity})`;
  }

  if (opacity === undefined || opacity >= 1) {
    return customBackground;
  }

  const rgb = hexToRgb(customBackground);
  if (rgb) {
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
  }
  return customBackground;
}
